(() => {
  'use strict';

  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  });

  class ConfigurationSetupError extends Error {}
  class PricingSetupError extends Error {}
  let generatedControlId = 0;

  function readRulePack(container) {
    const payload = container.querySelector('[data-shade-rule-pack]');
    if (!payload) throw new ConfigurationSetupError('Product configuration is unavailable.');
    try {
      return JSON.parse(payload.textContent);
    } catch (error) {
      throw new ConfigurationSetupError('Product configuration is unavailable.');
    }
  }

  function isRecord(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function validateRulePack(rulePack) {
    const strings = [
      rulePack?.id,
      rulePack?.productCode,
      rulePack?.fixed?.mechanism,
      rulePack?.fixed?.mountType,
      rulePack?.fixed?.installationFamily,
      rulePack?.pricing?.type,
      rulePack?.pricing?.source,
      rulePack?.pricing?.baseKey,
      rulePack?.pricing?.widthKey,
      rulePack?.pricing?.heightKey,
      rulePack?.pricing?.currency
    ];
    const steps = rulePack?.steps;
    const fractions = rulePack?.allowedFractions;
    const fabrics = rulePack?.fabrics;
    const dimensions = rulePack?.dimensions;
    const validSteps = Array.isArray(steps) &&
      steps.length > 0 &&
      new Set(steps).size === steps.length &&
      steps.every((step) => typeof step === 'string' && step.length > 0);
    const validFractions = Array.isArray(fractions) &&
      fractions.length > 0 &&
      fractions.every((fraction) => typeof fraction === 'string');
    const validFabrics = Array.isArray(fabrics) &&
      fabrics.length > 0 &&
      fabrics.every((fabric) => (
        isRecord(fabric) &&
        typeof fabric.code === 'string' &&
        typeof fabric.name === 'string' &&
        typeof fabric.lightControl === 'string'
      ));
    const validDimensions = isRecord(dimensions?.width) &&
      isRecord(dimensions?.height) &&
      typeof dimensions.width.min === 'number' &&
      typeof dimensions.width.max === 'number' &&
      Number.isFinite(dimensions.width.min) &&
      Number.isFinite(dimensions.width.max) &&
      dimensions.width.min <= dimensions.width.max &&
      typeof dimensions.height.min === 'number' &&
      typeof dimensions.height.max === 'number' &&
      Number.isFinite(dimensions.height.min) &&
      Number.isFinite(dimensions.height.max) &&
      dimensions.height.min <= dimensions.height.max;

    if (
      !isRecord(rulePack) ||
      !Number.isInteger(rulePack.version) ||
      strings.some((value) => typeof value !== 'string' || value.length === 0) ||
      !isRecord(rulePack.fixed) ||
      typeof rulePack.fixed.minimumDepth !== 'number' ||
      !Number.isFinite(rulePack.fixed.minimumDepth) ||
      !validSteps ||
      !validFractions ||
      !validFabrics ||
      !validDimensions ||
      !isRecord(rulePack.pricing)
    ) {
      throw new ConfigurationSetupError('Product configuration is unavailable.');
    }
    return rulePack;
  }

  function validatePricingData(rulePack, pricingData) {
    if (!isRecord(pricingData)) {
      throw new PricingSetupError('Preview pricing is unavailable.');
    }
    const base = pricingData[rulePack.pricing.baseKey];
    const widthMap = pricingData[rulePack.pricing.widthKey];
    const heightMap = pricingData[rulePack.pricing.heightKey];
    const finiteMap = (value) => (
      isRecord(value) &&
      Object.keys(value).length > 0 &&
      Object.values(value).every((price) => (
        typeof price === 'number' && Number.isFinite(price)
      ))
    );
    if (
      typeof base !== 'number' ||
      !Number.isFinite(base) ||
      !finiteMap(widthMap) ||
      !finiteMap(heightMap)
    ) {
      throw new PricingSetupError('Preview pricing is unavailable.');
    }
    return pricingData;
  }

  function labelFor(value) {
    return String(value || '')
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  function renderProgress(form, activeStep) {
    form.querySelectorAll('[data-progress-step]').forEach((item) => {
      const active = item.dataset.progressStep === activeStep;
      item.toggleAttribute('aria-current', active);
      if (active) item.setAttribute('aria-current', 'step');
    });
  }

  function setup(container) {
    const form = container.querySelector('[data-customizer-form]');
    if (!form) return;

    const core = window.ShadeCustomizerCore;
    const formError = form.querySelector('[data-form-error]');
    const finalAction = form.querySelector('[data-final-action]');
    const stickyBar = form.querySelector('[data-customizer-sticky-action]');
    const stickyAction = form.querySelector('[data-sticky-continue]');
    const previewPrice = form.querySelector('[data-preview-price]');
    const stickyPrice = form.querySelector('[data-sticky-price]');
    const configurationResult = form.querySelector('[data-configuration-result]');
    const reviewList = form.querySelector('[data-review-list]');
    const propertyControls = [
      form.querySelector('[data-property-color]'),
      form.querySelector('[data-property-light-control]'),
      form.querySelector('[data-property-width]'),
      form.querySelector('[data-property-height]'),
      form.querySelector('[data-property-depth]')
    ].filter(Boolean);

    function clearLineItemProperties() {
      propertyControls.forEach((control) => {
        control.value = '';
      });
    }

    function renderReviewUnavailable() {
      if (!reviewList) return;
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = 'Review';
      dd.textContent = 'Unavailable';
      reviewList.replaceChildren(dt, dd);
    }

    function failClosed(message) {
      if (formError) formError.textContent = message;
      if (finalAction) {
        finalAction.disabled = true;
        finalAction.textContent = 'Configuration unavailable';
      }
      if (stickyAction) {
        stickyAction.disabled = true;
        stickyAction.setAttribute('aria-disabled', 'true');
        stickyAction.textContent = 'Configuration unavailable';
      }
      if (previewPrice) previewPrice.textContent = '—';
      if (stickyPrice) stickyPrice.textContent = '—';
      if (configurationResult) configurationResult.value = '';
      clearLineItemProperties();
      renderReviewUnavailable();
    }

    const requiredSurfaceHooks = [
      formError,
      finalAction,
      stickyBar,
      stickyAction,
      previewPrice,
      stickyPrice,
      configurationResult,
      reviewList
    ];
    if (
      requiredSurfaceHooks.some((node) => !node) ||
      propertyControls.length !== 5
    ) {
      failClosed('Product configuration is unavailable.');
      return;
    }

    if (!core) {
      failClosed('Product configuration is unavailable.');
      return;
    }

    let rulePack;
    let pricingData;
    let steps;
    try {
      rulePack = validateRulePack(readRulePack(container));
      pricingData = validatePricingData(rulePack, window[rulePack.pricing.source]);
      const stepElements = Array.from(form.querySelectorAll('[data-customizer-step]'));
      const stepById = new Map(stepElements.map((step) => [
        step.dataset.customizerStep,
        step
      ]));
      steps = rulePack.steps.map((stepId) => stepById.get(stepId));
      if (steps.some((step) => !step)) {
        throw new ConfigurationSetupError('Product configuration is unavailable.');
      }
    } catch (error) {
      failClosed(
        error instanceof PricingSetupError
          ? 'Preview pricing is unavailable.'
          : 'Product configuration is unavailable.'
      );
      return;
    }

    const purchaseEnabled = container.dataset.purchaseEnabled === 'true';
    const completed = new Set();
    const fabricRadios = Array.from(form.querySelectorAll('[data-fabric-code]'));
    const fabricGrid = form.querySelector('[data-fabric-grid]');
    const fabricError = form.querySelector('[data-field-error="fabricCode"]');
    const filterActions = Array.from(form.querySelectorAll('[data-light-control-filter]'));
    const requiredStepHooks = steps.every((step) => (
      step.querySelector('[data-step-trigger]') &&
      step.querySelector('[data-step-panel]') &&
      step.querySelector('[data-step-summary]')
    ));
    if (
      !requiredStepHooks ||
      (
        rulePack.steps.includes('fabric') &&
        (
          fabricRadios.length === 0 ||
          !fabricGrid ||
          !fabricError?.id ||
          filterActions.length === 0
        )
      )
    ) {
      failClosed('Product configuration is unavailable.');
      return;
    }
    let currentIndex = 0;
    let priceSucceeded = false;
    let latestPricingError = '';
    let failed = false;
    const visualViewport = window.visualViewport;
    const mobileViewport = window.matchMedia?.('(max-width: 640px)');
    let viewportFrame = 0;
    let stickyResizeObserver = null;

    function stickyHeight() {
      const height = stickyBar.getBoundingClientRect?.().height;
      return Number.isFinite(height) ? Math.max(0, Math.ceil(height)) : 0;
    }

    function syncStickyClearance() {
      form.style.setProperty('--customizer-sticky-height', `${stickyHeight()}px`);
    }

    function keepFocusedControlVisible() {
      viewportFrame = 0;
      syncStickyClearance();
      if (!(mobileViewport?.matches ?? window.innerWidth <= 640)) return;
      const active = document.activeElement;
      if (!active || !form.contains(active) || typeof active.getBoundingClientRect !== 'function') return;
      const rect = active.getBoundingClientRect();
      const viewportHeight = visualViewport?.height || window.innerHeight || 0;
      const visibleBottom = viewportHeight - stickyHeight() - 16;
      if (rect.top < 16 || rect.bottom > visibleBottom) {
        active.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'nearest'
        });
      }
    }

    function scheduleViewportSync() {
      if (viewportFrame) window.cancelAnimationFrame?.(viewportFrame);
      const requestFrame = window.requestAnimationFrame || ((callback) => callback());
      viewportFrame = requestFrame(keepFocusedControlVisible);
    }

    function cleanupViewportSync() {
      if (viewportFrame) window.cancelAnimationFrame?.(viewportFrame);
      viewportFrame = 0;
      form.removeEventListener('focusin', scheduleViewportSync);
      visualViewport?.removeEventListener('resize', scheduleViewportSync);
      visualViewport?.removeEventListener('scroll', scheduleViewportSync);
      window.removeEventListener?.('resize', scheduleViewportSync);
      stickyResizeObserver?.disconnect();
      document.removeEventListener('shopify:section:unload', handleSectionUnload);
      form.style.removeProperty('--customizer-sticky-height');
    }

    function handleSectionUnload(event) {
      if (event.target !== container && !event.target?.contains?.(container)) return;
      cleanupViewportSync();
    }

    function bindViewportSync() {
      syncStickyClearance();
      form.addEventListener('focusin', scheduleViewportSync);
      visualViewport?.addEventListener('resize', scheduleViewportSync);
      visualViewport?.addEventListener('scroll', scheduleViewportSync);
      window.addEventListener?.('resize', scheduleViewportSync);
      if (typeof window.ResizeObserver === 'function') {
        stickyResizeObserver = new window.ResizeObserver(scheduleViewportSync);
        stickyResizeObserver.observe(stickyBar);
      }
      document.addEventListener('shopify:section:unload', handleSectionUnload);
    }

    const value = (selector) => form.querySelector(selector)?.value ?? '';
    const selectedFabric = () => fabricRadios.find((radio) => radio.checked) || null;
    const fieldControls = (field) => ({
      fabricCode: fabricRadios,
      depthConfirmed: [form.querySelector('[data-depth-confirmation]')],
      widthWhole: [form.querySelector('[data-width-whole]')],
      widthFraction: [form.querySelector('[data-width-fraction]')],
      heightWhole: [form.querySelector('[data-height-whole]')],
      heightFraction: [form.querySelector('[data-height-fraction]')],
      quantity: [form.querySelector('[data-quantity]')]
    })[field]?.filter(Boolean) || [];

    function appendDescription(control, id) {
      if (!control || !id) return;
      const ids = new Set(String(control.getAttribute('aria-describedby') || '')
        .split(/\s+/)
        .filter(Boolean));
      ids.add(id);
      control.setAttribute('aria-describedby', [...ids].join(' '));
    }

    function bindFabricAccessibility() {
      fabricRadios.forEach((radio) => appendDescription(radio, fabricError.id));
      if (fabricGrid) {
        const fabricStep = steps.find(
          (step) => step.dataset.customizerStep === 'fabric'
        );
        const trigger = fabricStep?.querySelector('[data-step-trigger]');
        const controlledPanelId = trigger?.getAttribute('aria-controls');
        const triggerId = trigger?.getAttribute('id') ||
          (controlledPanelId
            ? `${controlledPanelId}-Trigger`
            : `ShadeConfiguratorControl-${++generatedControlId}`);
        if (trigger && triggerId) {
          trigger.setAttribute('id', triggerId);
          fabricGrid.setAttribute('aria-labelledby', triggerId);
        }
        fabricGrid.setAttribute('role', 'radiogroup');
        appendDescription(fabricGrid, fabricError.id);
      }
    }

    function configuration() {
      const fabric = selectedFabric();
      return {
        productCode: rulePack.productCode,
        fabricCode: fabric?.value || '',
        colorName: fabric?.dataset.colorName || '',
        mountType: rulePack.fixed.mountType,
        widthWhole: Number(value('[data-width-whole]')),
        widthFraction: value('[data-width-fraction]') || '0',
        heightWhole: Number(value('[data-height-whole]')),
        heightFraction: value('[data-height-fraction]') || '0',
        depthConfirmed: Boolean(form.querySelector('[data-depth-confirmation]')?.checked),
        quantity: Number(value('[data-quantity]')),
        roomName: value('[name="properties[Room Name]"]'),
        customerNote: value('[name="properties[Customer Note]"]')
      };
    }

    function applyOpenStep(index, focus = false) {
      currentIndex = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, stepIndex) => {
        const trigger = step.querySelector('[data-step-trigger]');
        const panel = step.querySelector('[data-step-panel]');
        const current = stepIndex === currentIndex;
        step.classList.toggle('is-current', current);
        trigger.setAttribute('aria-expanded', String(current));
        panel.hidden = !current;
      });
      renderProgress(form, steps[currentIndex].dataset.customizerStep);
      const progress = form.querySelector('[data-progress-current]');
      if (progress) progress.textContent = String(currentIndex + 1);
      if (focus) steps[currentIndex].querySelector('[data-step-trigger]').focus();
    }

    function openStep(index, focus = false) {
      const canOpen = index === 0 || steps.slice(0, index)
        .every((step) => completed.has(step.dataset.customizerStep));
      if (canOpen) applyOpenStep(index, focus);
    }

    function fixedSummary(current) {
      const facts = [
        `${labelFor(rulePack.fixed.mountType)} Mount`,
        labelFor(rulePack.fixed.installationFamily)
      ];
      if (current.depthConfirmed && Number(rulePack.fixed.minimumDepth) > 0) {
        facts.push(`${rulePack.fixed.minimumDepth} in confirmed`);
      }
      return facts.join(' · ');
    }

    function renderStepSummaries(current, review, validation) {
      const summaries = {
        fabric: review?.fabric
          ? `${review.fabric} · ${review.lightControl}`
          : 'Select a fabric',
        fit: fixedSummary(current),
        size: review?.width
          ? `${review.width} W × ${review.height} H`
          : 'Select width and height',
        details: `Quantity ${current.quantity || 1}`,
        review: validation.valid && priceSucceeded
          ? 'Ready to review'
          : 'Complete the steps above'
      };

      steps.forEach((step) => {
        const summary = step.querySelector('[data-step-summary]');
        if (summary) {
          summary.textContent = summaries[step.dataset.customizerStep] ||
            labelFor(step.dataset.customizerStep);
        }
      });
      renderCompletionState();
    }

    function renderCompletionState() {
      steps.forEach((step) => {
        step.classList.toggle('is-complete', completed.has(step.dataset.customizerStep));
      });
    }

    function renderFieldErrors(errors) {
      form.querySelectorAll('[data-field-error]').forEach((node) => {
        node.textContent = '';
      });

      const invalidFields = new Set(errors.map(({ field }) => field));
      for (const { field, message } of errors) {
        const target = form.querySelector(`[data-field-error="${field}"]`);
        if (target) target.textContent = message;
      }

      for (const field of [
        'fabricCode',
        'depthConfirmed',
        'widthWhole',
        'widthFraction',
        'heightWhole',
        'heightFraction',
        'quantity'
      ]) {
        fieldControls(field).forEach((control) => {
          control.setAttribute('aria-invalid', String(invalidFields.has(field)));
        });
      }
    }

    function focusField(field) {
      const control = fieldControls(field).find((candidate) => !candidate.disabled);
      control?.focus();
    }

    function focusStatus(message) {
      if (!formError) return;
      formError.textContent = message;
      formError.setAttribute('tabindex', '-1');
      formError.focus();
    }

    function renderReview(review) {
      const rows = [
        ['Product', review.mechanism],
        ['Fabric', `${review.fabric} · ${review.lightControl}`],
        ['Mount', review.mount],
        ['Fit', review.depth],
        ['Size', `${review.width} W × ${review.height} H`],
        ['Quantity', String(review.quantity)]
      ];
      if (review.roomName) rows.push(['Room', review.roomName]);
      if (review.customerNote) rows.push(['Note', review.customerNote]);

      reviewList.replaceChildren(...rows.flatMap(([term, description]) => {
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = term;
        dd.textContent = description;
        return [dt, dd];
      }));
    }

    function syncProperties(current, review) {
      form.querySelector('[data-property-color]').value = review.fabric;
      form.querySelector('[data-property-light-control]').value = review.lightControl;
      form.querySelector('[data-property-width]').value = review.width;
      form.querySelector('[data-property-height]').value = review.height;
      form.querySelector('[data-property-depth]').value = current.depthConfirmed
        ? `${rulePack.fixed.minimumDepth} in`
        : '';
    }

    function updateFabricVisibility(filter = 'ALL') {
      form.querySelectorAll('[data-fabric-option]').forEach((option) => {
        const matchesFilter = filter === 'ALL' || option.dataset.lightControl === filter;
        option.hidden = !matchesFilter;
      });
    }

    function revokeCompletionFrom(index) {
      steps.slice(index).forEach((step) => {
        completed.delete(step.dataset.customizerStep);
      });
    }

    function revalidateCompleted(current) {
      const invalidIndex = steps.findIndex((step) => (
        completed.has(step.dataset.customizerStep) &&
        !core.validateStep(step.dataset.customizerStep, current, rulePack).valid
      ));
      if (invalidIndex < 0) return;
      revokeCompletionFrom(invalidIndex);
      if (currentIndex > invalidIndex) applyOpenStep(invalidIndex);
    }

    function actionLabel(validation) {
      if (purchaseEnabled && validation.valid && priceSucceeded) {
        return 'Add custom shade to cart';
      }
      if (!purchaseEnabled) return 'Review configuration';
      if (!validation.valid) return 'Complete configuration';
      return 'Preview pricing unavailable';
    }

    function syncActions(validation) {
      const eligible = purchaseEnabled && validation.valid && priceSucceeded;
      const label = actionLabel(validation);
      finalAction.disabled = !eligible;
      finalAction.textContent = label;

      const currentStep = steps[currentIndex];
      const onReview = currentStep?.dataset.customizerStep === 'review' ||
        !currentStep?.querySelector('[data-step-continue]');
      stickyAction.disabled = false;
      stickyAction.setAttribute('aria-disabled', String(onReview && !eligible));
      stickyAction.classList.toggle('is-disabled', onReview && !eligible);
      stickyAction.textContent = onReview ? label : 'Continue';
      scheduleViewportSync();
      return eligible;
    }

    function sync() {
      if (failed) {
        return {
          current: configuration(),
          validation: { valid: false, errors: [] },
          eligible: false
        };
      }

      const reconciled = core.reconcileConfiguration(configuration(), rulePack);
      const current = reconciled.configuration;
      const validation = core.validateConfiguration(current, rulePack);
      revalidateCompleted(current);
      renderFieldErrors(validation.errors);
      formError.textContent = '';

      let preview = null;
      latestPricingError = '';
      try {
        const unitPrice = core.calculatePreviewPrice(current, rulePack, pricingData);
        preview = unitPrice * (current.quantity || 1);
        if (!Number.isFinite(unitPrice) || !Number.isFinite(preview)) {
          throw new PricingSetupError('Preview pricing is unavailable.');
        }
        priceSucceeded = true;
      } catch (error) {
        priceSucceeded = false;
        latestPricingError = 'Preview pricing is unavailable.';
        formError.textContent = latestPricingError;
      }

      const priceText = priceSucceeded ? money.format(preview) : '—';
      previewPrice.textContent = priceText;
      stickyPrice.textContent = priceText;

      let review = null;
      try {
        review = core.buildReview(current, rulePack);
      } catch (error) {
        review = null;
      }
      if (validation.valid && review) {
        renderReview(review);
        syncProperties(current, review);
      } else {
        renderReviewUnavailable();
        clearLineItemProperties();
      }

      configurationResult.value =
        validation.valid && priceSucceeded
          ? JSON.stringify(core.buildConfigurationResult(current, rulePack, preview))
          : '';
      renderStepSummaries(current, review, validation);
      const eligible = syncActions(validation);
      return { current, validation, eligible };
    }

    function safeSync() {
      try {
        return sync();
      } catch (error) {
        failed = true;
        failClosed(
          error instanceof PricingSetupError
            ? 'Preview pricing is unavailable.'
            : 'Product configuration is unavailable.'
        );
        return {
          current: configuration(),
          validation: { valid: false, errors: [] },
          eligible: false
        };
      }
    }

    function surfaceBlocked(state) {
      if (!state.validation.valid) {
        renderFieldErrors(state.validation.errors);
        focusStatus('Complete the highlighted configuration fields.');
        if (state.validation.errors[0]) focusField(state.validation.errors[0].field);
      } else if (!purchaseEnabled) {
        focusStatus('Purchasing is not yet enabled for this custom product.');
      } else {
        focusStatus(latestPricingError || 'Preview pricing is unavailable.');
      }
    }

    bindFabricAccessibility();
    bindViewportSync();

    steps.forEach((step, index) => {
      step.querySelector('[data-step-trigger]').addEventListener('click', () => openStep(index));
      step.querySelector('[data-step-continue]')?.addEventListener('click', () => {
        const state = safeSync();
        if (failed) return;
        const stepId = step.dataset.customizerStep;
        const result = core.validateStep(stepId, state.current, rulePack);
        renderFieldErrors(result.errors);
        if (result.valid) {
          completed.add(stepId);
          applyOpenStep(Math.min(index + 1, steps.length - 1), true);
          safeSync();
        } else {
          revokeCompletionFrom(index);
          renderCompletionState();
          focusField(result.errors[0]?.field);
        }
      });
    });

    filterActions.forEach((button) => {
      button.addEventListener('click', () => {
        form.querySelectorAll('[data-light-control-filter]').forEach((candidate) => {
          const active = candidate === button;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-pressed', String(active));
        });
        updateFabricVisibility(button.dataset.lightControlFilter);
      });
    });

    stickyAction.addEventListener('click', () => {
      const currentStep = steps[currentIndex];
      const continueButton = currentStep.querySelector('[data-step-continue]');
      if (continueButton) {
        continueButton.click();
        return;
      }
      const state = safeSync();
      if (state.eligible) form.requestSubmit(finalAction);
      else surfaceBlocked(state);
    });

    form.addEventListener('input', safeSync);
    form.addEventListener('change', safeSync);
    form.addEventListener('submit', (event) => {
      const state = safeSync();
      if (!state.eligible) {
        event.preventDefault();
        surfaceBlocked(state);
      }
    });

    updateFabricVisibility();
    applyOpenStep(0);
    safeSync();
  }

  function setupFlat(container) {
    const form = container.querySelector('[data-customizer-form]');
    if (!form) return;

    const formError = form.querySelector('[data-form-error]');
    const finalAction = form.querySelector('[data-final-action]');
    const previewPrice = form.querySelector('[data-preview-price]');
    const configurationResult = form.querySelector('[data-configuration-result]');
    const reviewList = form.querySelector('[data-review-list]');
    const propertyControls = [
      form.querySelector('[data-property-color]'),
      form.querySelector('[data-property-light-control]'),
      form.querySelector('[data-property-width]'),
      form.querySelector('[data-property-height]'),
      form.querySelector('[data-property-depth]')
    ].filter(Boolean);

    const clearLineItemProperties = () => propertyControls.forEach((control) => { control.value = ''; });
    const renderReviewUnavailable = () => {
      if (!reviewList) return;
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = 'Review';
      dd.textContent = 'Unavailable';
      reviewList.replaceChildren(dt, dd);
    };
    const failClosed = (message) => {
      if (formError) formError.textContent = message;
      if (finalAction) {
        finalAction.disabled = true;
        finalAction.textContent = 'Configuration unavailable';
      }
      if (previewPrice) previewPrice.textContent = '—';
      if (configurationResult) configurationResult.value = '';
      clearLineItemProperties();
      renderReviewUnavailable();
    };

    const requiredSurfaceHooks = [formError, finalAction, previewPrice, configurationResult, reviewList];
    if (requiredSurfaceHooks.some((node) => !node) || propertyControls.length !== 5 || !window.ShadeCustomizerCore) {
      failClosed('Product configuration is unavailable.');
      return;
    }

    let rulePack;
    let pricingData;
    let steps;
    try {
      rulePack = validateRulePack(readRulePack(container));
      pricingData = validatePricingData(rulePack, window[rulePack.pricing.source]);
      const stepById = new Map(Array.from(form.querySelectorAll('[data-customizer-step]')).map((step) => [step.dataset.customizerStep, step]));
      steps = rulePack.steps.map((stepId) => stepById.get(stepId));
      if (steps.some((step) => !step) || !steps.every((step) => step.querySelector('[data-step-panel]'))) {
        throw new ConfigurationSetupError('Product configuration is unavailable.');
      }
    } catch (error) {
      failClosed(error instanceof PricingSetupError ? 'Preview pricing is unavailable.' : 'Product configuration is unavailable.');
      return;
    }

    const core = window.ShadeCustomizerCore;
    const purchaseEnabled = container.dataset.purchaseEnabled === 'true';
    const fabricRadios = Array.from(form.querySelectorAll('[data-fabric-code]'));
    const fabricGrid = form.querySelector('[data-fabric-grid]');
    const fabricError = form.querySelector('[data-field-error="fabricCode"]');
    const filterActions = Array.from(form.querySelectorAll('[data-light-control-filter]'));
    if (rulePack.steps.includes('fabric') && (!fabricRadios.length || !fabricGrid || !fabricError?.id || !filterActions.length)) {
      failClosed('Product configuration is unavailable.');
      return;
    }

    let priceSucceeded = false;
    let latestPricingError = '';
    let failed = false;
    const value = (selector) => form.querySelector(selector)?.value ?? '';
    const selectedFabric = () => fabricRadios.find((radio) => radio.checked) || null;
    const fieldControls = (field) => ({
      fabricCode: fabricRadios,
      depthConfirmed: [form.querySelector('[data-depth-confirmation]')],
      widthWhole: [form.querySelector('[data-width-whole]')],
      widthFraction: [form.querySelector('[data-width-fraction]')],
      heightWhole: [form.querySelector('[data-height-whole]')],
      heightFraction: [form.querySelector('[data-height-fraction]')],
      quantity: [form.querySelector('[data-quantity]')]
    })[field]?.filter(Boolean) || [];
    const appendDescription = (control, id) => {
      if (!control || !id) return;
      const ids = new Set(String(control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      ids.add(id);
      control.setAttribute('aria-describedby', [...ids].join(' '));
    };

    fabricRadios.forEach((radio) => appendDescription(radio, fabricError.id));
    fabricGrid.setAttribute('role', 'radiogroup');
    appendDescription(fabricGrid, fabricError.id);
    const fabricHeading = steps.find((step) => step.dataset.customizerStep === 'fabric')?.querySelector('[data-step-heading]');
    const fabricHeadingId = fabricHeading?.getAttribute('id') || `ShadeConfiguratorHeading-${++generatedControlId}`;
    if (fabricHeading) fabricHeading.setAttribute('id', fabricHeadingId);
    fabricGrid.setAttribute('aria-labelledby', fabricHeadingId);

    const configuration = () => {
      const fabric = selectedFabric();
      return {
        productCode: rulePack.productCode,
        fabricCode: fabric?.value || '',
        colorName: fabric?.dataset.colorName || '',
        mountType: rulePack.fixed.mountType,
        widthWhole: Number(value('[data-width-whole]')),
        widthFraction: value('[data-width-fraction]') || '0',
        heightWhole: Number(value('[data-height-whole]')),
        heightFraction: value('[data-height-fraction]') || '0',
        depthConfirmed: Boolean(form.querySelector('[data-depth-confirmation]')?.checked),
        quantity: Number(value('[data-quantity]')),
        roomName: value('[name="properties[Room Name]"]'),
        customerNote: value('[name="properties[Customer Note]"]')
      };
    };
    const renderFieldErrors = (errors) => {
      form.querySelectorAll('[data-field-error]').forEach((node) => { node.textContent = ''; });
      const invalidFields = new Set(errors.map(({ field }) => field));
      errors.forEach(({ field, message }) => {
        const target = form.querySelector(`[data-field-error="${field}"]`);
        if (target) target.textContent = message;
      });
      ['fabricCode', 'depthConfirmed', 'widthWhole', 'widthFraction', 'heightWhole', 'heightFraction', 'quantity'].forEach((field) => {
        fieldControls(field).forEach((control) => control.setAttribute('aria-invalid', String(invalidFields.has(field))));
      });
    };
    const renderReview = (review) => {
      const rows = [
        ['Product', review.mechanism],
        ['Fabric', `${review.fabric} · ${review.lightControl}`],
        ['Mount', review.mount],
        ['Fit', review.depth],
        ['Size', `${review.width} W × ${review.height} H`],
        ['Quantity', String(review.quantity)]
      ];
      if (review.roomName) rows.push(['Room', review.roomName]);
      if (review.customerNote) rows.push(['Note', review.customerNote]);
      reviewList.replaceChildren(...rows.flatMap(([term, description]) => {
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = term;
        dd.textContent = description;
        return [dt, dd];
      }));
    };
    const syncProperties = (current, review) => {
      form.querySelector('[data-property-color]').value = review.fabric;
      form.querySelector('[data-property-light-control]').value = review.lightControl;
      form.querySelector('[data-property-width]').value = review.width;
      form.querySelector('[data-property-height]').value = review.height;
      form.querySelector('[data-property-depth]').value = current.depthConfirmed ? `${rulePack.fixed.minimumDepth} in` : '';
    };
    const updateFabricVisibility = (filter = 'ALL') => form.querySelectorAll('[data-fabric-option]').forEach((option) => {
      const matchesFilter = filter === 'ALL' || option.dataset.lightControl === filter;
      option.hidden = !matchesFilter;
    });
    const actionLabel = (validation) => {
      if (purchaseEnabled && validation.valid && priceSucceeded) return 'Add custom shade to cart';
      if (!purchaseEnabled) return 'Review configuration';
      if (!validation.valid) return 'Complete configuration';
      return 'Preview pricing unavailable';
    };
    const sync = (event) => {
      const reconciled = core.reconcileConfiguration(configuration(), rulePack);
      const current = reconciled.configuration;
      const validation = core.validateConfiguration(current, rulePack);
      renderFieldErrors(validation.errors);
      formError.textContent = '';
      let preview = null;
      latestPricingError = '';
      try {
        const unitPrice = core.calculatePreviewPrice(current, rulePack, pricingData);
        preview = unitPrice * (current.quantity || 1);
        if (!Number.isFinite(unitPrice) || !Number.isFinite(preview)) throw new PricingSetupError('Preview pricing is unavailable.');
        priceSucceeded = true;
      } catch (error) {
        priceSucceeded = false;
        latestPricingError = 'Preview pricing is unavailable.';
        formError.textContent = latestPricingError;
      }
      previewPrice.textContent = priceSucceeded ? money.format(preview) : '—';
      let review = null;
      try { review = core.buildReview(current, rulePack); } catch (error) { review = null; }
      if (validation.valid && review) {
        renderReview(review);
        syncProperties(current, review);
      } else {
        renderReviewUnavailable();
        clearLineItemProperties();
      }
      configurationResult.value = validation.valid && priceSucceeded ? JSON.stringify(core.buildConfigurationResult(current, rulePack, preview)) : '';
      const activeStep = validation.valid && priceSucceeded
        ? 'review'
        : event?.target?.closest?.('[data-customizer-step]')?.dataset.customizerStep ||
          form.querySelector('[data-progress-step][aria-current]')?.dataset.progressStep ||
          'fabric';
      renderProgress(form, activeStep);
      const eligible = purchaseEnabled && validation.valid && priceSucceeded;
      finalAction.disabled = !eligible;
      finalAction.textContent = actionLabel(validation);
      return { current, validation, eligible };
    };
    const safeSync = (event) => {
      if (failed) return { current: configuration(), validation: { valid: false, errors: [] }, eligible: false };
      try { return sync(event); } catch (error) {
        failed = true;
        failClosed(error instanceof PricingSetupError ? 'Preview pricing is unavailable.' : 'Product configuration is unavailable.');
        return { current: configuration(), validation: { valid: false, errors: [] }, eligible: false };
      }
    };
    const surfaceBlocked = (state) => {
      if (!state.validation.valid) formError.textContent = 'Complete the highlighted configuration fields.';
      else if (!purchaseEnabled) formError.textContent = 'This configuration can be reviewed, but ordering is not available yet.';
      else formError.textContent = latestPricingError || 'Preview pricing is unavailable.';
      formError.setAttribute('tabindex', '-1');
      formError.focus();
    };

    filterActions.forEach((button) => button.addEventListener('click', () => {
      filterActions.forEach((candidate) => {
        const active = candidate === button;
        candidate.classList.toggle('is-active', active);
        candidate.setAttribute('aria-pressed', String(active));
      });
      updateFabricVisibility(button.dataset.lightControlFilter);
      renderProgress(form, 'fabric');
    }));
    form.addEventListener('input', safeSync);
    form.addEventListener('change', safeSync);
    form.addEventListener('submit', (event) => {
      const state = safeSync();
      if (!state.eligible) {
        event.preventDefault();
        surfaceBlocked(state);
      }
    });
    updateFabricVisibility();
    safeSync();
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-shade-configurator]').forEach(setupFlat);
  });
})();
